import { useOktaAuth } from "@okta/okta-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AUTH_BYPASS } from "@/auth-bypass";
import { useLogoutLoading } from "@/contexts/logout-loading-context";

export const Login = () => {
	const [isDisabled, setIsDisabled] = useState(false);
	const { authState, oktaAuth } = useOktaAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { setIsLoggingOut } = useLogoutLoading();

	const redirectUrl = searchParams.get("redirect") || "/tenants";

	useEffect(() => {
		setIsLoggingOut(false);
	}, [setIsLoggingOut]);

	useEffect(() => {
		if (AUTH_BYPASS || authState?.isAuthenticated) {
			navigate(redirectUrl, { replace: true });
		}
	}, [authState?.isAuthenticated, navigate, redirectUrl]);

	const handleLogin = useCallback(async () => {
		setIsDisabled(true);
		try {
			await oktaAuth.signInWithRedirect({
				originalUri: redirectUrl,
			});
		} catch {
			setIsDisabled(false);
		}
	}, [oktaAuth, redirectUrl]);

	if (AUTH_BYPASS || authState?.isAuthenticated) {
		return null;
	}

	if (authState === null) {
		return (
			<div className="flex min-h-[400px] items-center justify-center">
				<div className="text-center">
					<div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-current border-r-transparent border-solid align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]" />
					<p className="text-gray-600 text-lg">Loading...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-[400px] flex-col items-center justify-center space-y-8 rounded-lg bg-white px-8 py-8 shadow-md">
			<div className="text-center">
				<h1 className="whitespace-nowrap font-bold text-3xl text-gray-900">
					Anti-Fraud Admin Portal
				</h1>
				<p className="mt-2 text-gray-600 text-sm">Please sign in to continue</p>
			</div>
			<button
				disabled={isDisabled}
				onClick={handleLogin}
				className="group relative flex w-full max-w-xs cursor-pointer justify-center rounded-md border border-transparent bg-accent bg-indigo-600 px-4 py-3 font-medium text-sm text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
				type="button"
			>
				<svg
					className="mr-2 h-5 w-5"
					fill="currentColor"
					viewBox="0 0 20 20"
					xmlns="http://www.w3.org/2000/svg"
				>
					<title>Okta Logo</title>

					<path
						fillRule="evenodd"
						d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
						clipRule="evenodd"
					/>
				</svg>
				{isDisabled ? "Redirecting..." : "Sign in with Okta"}
			</button>
		</div>
	);
};
